/**
 * The layer stack at runtime.
 *
 * `shared/format.ts` describes what a layer *is* on disk. This is what it is
 * while someone is working: metadata plus a live canvas, composited in order
 * into one surface that the 3D viewer samples and export consumes.
 *
 * Three things it exists to get right:
 *
 *   1. **The composite is the only thing anyone else sees.** The viewer binds to
 *      `stack.canvas` and export reads it. Nothing downstream knows how many
 *      layers there are, which is what keeps the layer feature from leaking into
 *      the viewer contract and the edge pipeline.
 *
 *   2. **Recompositing is rect-bounded.** A brush dab must not cost a full
 *      585x559 recomposite per pointer event — that is 1.3 MB of churn per
 *      frame, the exact waste the dirty-rect path was built to avoid.
 *
 *   3. **A placement is clipped to its face before it is rotated.** That
 *      ordering is the whole structural guarantee: no rotation, scale or drag
 *      can put a logo onto a neighbouring face or into the dead space.
 */
import type { BlendMode, FacePlacement, RegionId } from "../../shared/format";
import { CANVAS } from "../../shared/format";
import { resolvePlacement } from "../../shared/placement";
import { REGIONS } from "../../shared/regions";
import { paintRects } from "../../shared/selection";
import type { FabricSource } from "./fabric-source";
import type { DirtyRect } from "./paint";

export type LayerKind = "artwork" | "placement";

/**
 * How an in-progress stroke combines with the layer receiving it.
 *
 * Only these three, and the omissions are the point. `source-in`,
 * `destination-in` and `copy` **clear destination pixels outside the source
 * shape** — reach for one of those by mistake and a single dab erases the whole
 * layer around it. `source-atop` and `destination-out` both preserve the
 * destination outside the stroke, which is what makes them safe here.
 * ([MDN](https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation))
 */
export type StrokeMode = "source-over" | "destination-out" | "source-atop";

/**
 * Resolve the stroke's composite mode from the tool and the layer.
 *
 * Pure and exported so it can be tested, and because it is the **single source
 * of truth for both the preview and the commit**. Those are two different code
 * paths — the overlay in `compose()` and the write in `paint.ts` — and a paint
 * app where the preview and the result disagree is worse than one that cannot
 * preview at all.
 */
/**
 * The canvas operation a blend mode maps to.
 *
 * One function, two callers — the renderer and `mergeDown`. They used to carry
 * a ternary each, both spelling out `multiply`, and widening the union without
 * finding the second would have flattened every new mode to Normal at the
 * moment somebody merged. A merge that quietly changes the picture is the worst
 * kind of bug this file can have: the layer is gone by the time it shows.
 *
 * `add` is the odd one. Canvas has no additive BLEND mode; `lighter` is a
 * composite operation that sums the channels, which is what "Add" means
 * everywhere else and what the highlight step of the shading tutorial wants.
 * It clips to white rather than compressing, so it blows out sooner than
 * Screen — which is why both are offered.
 */
export function blendComposite(mode: BlendMode): GlobalCompositeOperation {
  switch (mode) {
    case "multiply":
      return "multiply";
    case "overlay":
      return "overlay";
    case "soft-light":
      return "soft-light";
    case "screen":
      return "screen";
    case "add":
      return "lighter";
    default:
      return "source-over";
  }
}

/** What a merge has to do about clipping, or why it must not happen. */
export type MergePlan = { ok: false; reason: string } | { ok: true; maskToLower: boolean };

/**
 * Whether merging `upper` into `lower` can leave the picture alone.
 *
 * The whole point of merge down is that the image does not change — the layer
 * stops existing, and by then there is nothing left to compare against. Every
 * property that changes where a layer's ink lands has to be answered for here,
 * and clipping is one: a clipped layer shows only where its base has pixels, so
 * merging it by drawing the whole thing puts ink in every place the clip was
 * hiding it from.
 *
 * Pure, and separate from `mergeDown`, because that needs a canvas and this is
 * where the reasoning lives — the four cases are the thing worth testing.
 */
export function planMerge(
  upper: Pick<RuntimeLayer, "visible" | "opacity" | "clipToBelow" | "name">,
  lower: Pick<RuntimeLayer, "clipToBelow" | "name">,
): MergePlan {
  /*
    Invisible ink must not become visible. Baking a hidden layer down is the
    same class of surprise as ignoring the clip: the picture changes at the
    moment the layer it came from disappears.
  */
  if (!upper.visible) return { ok: false, reason: `${upper.name} is hidden — show it first.` };
  if (upper.opacity <= 0) {
    return { ok: false, reason: `${upper.name} is fully transparent — raise its opacity first.` };
  }

  /*
    Clipped into its own base: `lower` is what was masking it, so mask by
    `lower` while merging and the result is what was on screen.
  */
  if (upper.clipToBelow && !lower.clipToBelow) return { ok: true, maskToLower: true };

  /*
    Both clipped: they share a base further down, and the merged layer keeps
    `lower`'s flag — so the mask is applied at composite time exactly as before.
    Masking here as well would be correct but redundant.
  */
  if (upper.clipToBelow && lower.clipToBelow) return { ok: true, maskToLower: false };

  /*
    Unclipped into clipped. The result inherits `lower`'s clipping, so ink that
    is currently unmasked would start being masked — a change no amount of
    masking here can prevent. Refusing beats silently losing part of a layer.
  */
  if (lower.clipToBelow) {
    return { ok: false, reason: `${lower.name} is clipped — stop its clipping first.` };
  }

  return { ok: true, maskToLower: false };
}

export function strokeCompositeMode(opts: {
  erasing: boolean;
  paintInsideOnly: boolean;
}): StrokeMode {
  // Erasing already only affects existing pixels, so alpha lock adds nothing on
  // top of it. Letting it win would produce `source-atop`, which erases nothing
  // whatsoever — the eraser would silently stop working on locked layers.
  if (opts.erasing) return "destination-out";
  return opts.paintInsideOnly ? "source-atop" : "source-over";
}

/**
 * Move one item within an array, clamping the destination.
 *
 * Pure and exported for the same reason as `strokeCompositeMode`: it is the
 * arithmetic behind **three** entry points — drag-and-drop, the row menu's move
 * up/down, and `⌥↑`/`⌥↓` — and they must agree exactly or a layer lands one
 * place off depending on how it was moved.
 *
 * Remove-then-insert rather than swap. Swapping is right for adjacent moves and
 * quietly wrong for every other distance: dragging a layer from the bottom to
 * the top would displace exactly one neighbour instead of shifting the stack.
 *
 * Returns false when nothing moved, which is what suppresses a pointless
 * recomposite on a drag that ended where it began.
 */
export function moveWithin<T>(list: T[], from: number, to: number): boolean {
  if (from < 0 || from >= list.length) return false;
  const target = Math.max(0, Math.min(list.length - 1, to));
  if (target === from) return false;
  const [item] = list.splice(from, 1);
  if (item === undefined) return false;
  list.splice(target, 0, item);
  return true;
}

export interface RuntimeLayer {
  id: string;
  name: string;
  kind: LayerKind;
  visible: boolean;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
  /** Show only where the layer below has pixels. See `clipBaseFor`. */
  clipToBelow?: boolean;
  /**
   * Pixels. Full-canvas for artwork; the source image's own size for a
   * placement, so scaling stays lossless until the moment of compositing.
   *
   * A canvas for both, rather than an ImageBitmap for placements: it costs the
   * same to draw and it leaves the door open to editing a placement in place.
   */
  source: HTMLCanvasElement;
  /** Placements only. */
  placement?: FacePlacement;
  /** Set when this layer follows a file on disk. */
  link?: { filename: string };
  /**
   * Set when this layer's pixels came from a fabric fill, so the fill stays
   * editable — see src/app/fabric-source.ts.
   *
   * Read through `fabricOf()` rather than directly: the record carries the
   * `rev` it was written at, and it only still describes these pixels while
   * that matches. A layer painted on since is artwork that started as a
   * fabric, and re-filling it would destroy the strokes.
   */
  fabric?: FabricSource;

  /**
   * Bumped whenever this layer's **pixels** change. Not a version number —
   * a change token, and the only correct use is comparing it to a previous
   * value.
   *
   * It exists because the panel re-renders on every recomposite, which during a
   * stroke means every frame. Anything derived from a layer's pixels — a
   * thumbnail now, an encoded PNG when saving later — has to know which layers
   * actually changed, or it redoes all of them sixty times a second.
   *
   * Reordering, hiding and renaming deliberately do **not** bump it: they change
   * the composite without changing any layer's own pixels.
   */
  rev: number;

  /**
   * Paint only where this layer already has pixels — "Paint inside only".
   *
   * **Honoured by both the preview and the commit** through
   * `strokeCompositeMode`. What is still missing is a way to turn it on: the
   * chip on the layer row is the next step.
   *
   * This is the shading unlock — painting a garment without painting the skin
   * around it is the recurring task in Roblox clothing, and the #1 confirmed
   * user pain from the original research.
   *
   * Not called `alphaLock` in the UI — that is jargon borrowed from other
   * editors. See docs/decisions/interaction-and-devices.md.
   */
  paintInsideOnly?: boolean;

  /**
   * Restrict this layer to a set of faces — "Only this face".
   *
   * **Scaffold; nothing reads this yet.** Clip the commit to these region rects
   * and the layer physically cannot leak onto another body part. This existed
   * in the v1 schema as a stored `clipToRegions` field and was cut for being
   * something no human should type; as a toggle on the selected layer it is the
   * same data arrived at the right way.
   *
   * Empty or absent means every face.
   */
  faces?: RegionId[];
}

export interface LayerStackOptions {
  /** Fires after a recomposite, with the area that changed. */
  onChange?: (rect: DirtyRect, touched: readonly RuntimeLayer[]) => void;
  /**
   * Fires once per completed `transaction` that actually changed the stack's
   * shape or a layer's metadata. The stack does not know what a caller does
   * with this; history is one subscriber, not a dependency.
   */
  onStructure?: (
    label: string,
    before: readonly RuntimeLayer[],
    after: readonly RuntimeLayer[],
  ) => void;
}

/**
 * Did anything a user would call a change actually happen?
 *
 * Compared field by field rather than by identity, because `transaction`
 * snapshots shallow copies and most operations mutate metadata in place — an
 * identity check would report every hide as a no-op.
 *
 * Pixels are deliberately not compared. A stroke is a `PixelEdit`, recorded
 * separately and far more cheaply than diffing two canvases would be.
 */
export function structureDiffers(a: readonly RuntimeLayer[], b: readonly RuntimeLayer[]): boolean {
  if (a.length !== b.length) return true;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (!x || !y) return true;
    if (
      x.id !== y.id ||
      x.name !== y.name ||
      x.visible !== y.visible ||
      x.opacity !== y.opacity ||
      x.blendMode !== y.blendMode ||
      // Compared as booleans: absent and false mean the same thing, and an old
      // project has the field absent while a new one that was toggled off has
      // it false. Comparing raw would report a change between two identical
      // documents and put an undo step on the stack for opening a file.
      Boolean(x.clipToBelow) !== Boolean(y.clipToBelow) ||
      x.paintInsideOnly !== y.paintInsideOnly ||
      // By content, not by reference: `setFaces` writes a fresh array every
      // time, so a reference check would report a change on every call and put
      // an undo step on the stack for setting the same restriction twice.
      facesKey(x.faces) !== facesKey(y.faces) ||
      x.placement !== y.placement
    ) {
      return true;
    }
  }
  return false;
}

/**
 * A face restriction as one comparable string.
 *
 * Sorted, so two restrictions naming the same faces in a different order are
 * the same restriction. Absent and empty both collapse to "" — the convention
 * the field is documented with, and the one that keeps an old project with no
 * restriction indistinguishable from a cleared one.
 */
function facesKey(faces: readonly RegionId[] | undefined): string {
  if (!faces || faces.length === 0) return "";
  return [...faces].sort().join(",");
}

const FULL: DirtyRect = { x: 0, y: 0, w: CANVAS.width, h: CANVAS.height };

let counter = 0;
function newId(): string {
  // randomUUID needs a secure context; the counter keeps ids unique on plain
  // http during local development rather than throwing at layer creation.
  //
  // **Both of these are module-scoped, and that is load-bearing.** An outfit
  // runs two stacks over one document, and `shared/pack.ts` stores every
  // garment's bitmaps together in a flat `layers/<id>.png` — so an id minted
  // twice is not a clash, it is one garment silently overwriting the other's
  // pixels. Moving `counter` inside `createLayerStack` would look like tidying
  // and would do exactly that on plain http. There is a test.
  counter += 1;
  return globalThis.crypto?.randomUUID?.() ?? `layer-${counter}`;
}

function blankCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

/** Draw an image onto a fresh canvas at its natural size. */
function canvasFrom(image: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const c = blankCanvas(w, h);
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  ctx.drawImage(image, 0, 0, w, h);
  return c;
}

export function createLayerStack(opts: LayerStackOptions = {}) {
  const canvas = blankCanvas(CANVAS.width, CANVAS.height);
  const maybeCtx = canvas.getContext("2d");
  if (!maybeCtx) throw new Error("2D context unavailable");
  const ctx: CanvasRenderingContext2D = maybeCtx;

  /** Bottom first — index 0 paints first. The panel shows this reversed. */
  const layers: RuntimeLayer[] = [];
  let activeId: string | null = null;
  /** Set while a transaction is open, so nested ones join it rather than nest. */
  let inTransaction = false;

  /**
   * The in-progress stroke, drawn directly above the layer receiving it.
   *
   * The brush renders to its own scratch canvas and only commits once, on
   * pointerup. Between those two moments the stroke exists nowhere in the stack,
   * so without this it would be invisible — and worse, invisible *in 3D*, since
   * the viewer samples the composite. Watching a stroke land on the body while
   * you draw is the demo; losing it to fix the brush would be a bad trade.
   *
   * Opacity is read through a function rather than copied, because the brush
   * owns that value and a stale copy would show the wrong preview.
   */
  /**
   * A what-if, drawn above the stack and owned by nobody.
   *
   * Never serialised, never in an undo entry, never in an export — `compose()`
   * is the only thing that knows it exists, which is exactly the scope a
   * preview should have.
   */
  let preview: HTMLCanvasElement | null = null;

  let overlay: {
    source: HTMLCanvasElement;
    opacity: () => number;
    mode: () => StrokeMode;
    /** True only while a stroke is in flight, so compose can skip the scratch. */
    active: () => boolean;
  } | null = null;

  /**
   * Scratch buffer for compositing a stroke *with* its layer.
   *
   * Allocated once and reused. A 585x559 canvas is 1.3 MB; allocating one per
   * frame during a drag is the kind of garbage that shows up as stutter on the
   * low-end hardware this audience actually uses.
   *
   * Deliberately not `willReadFrequently`: nothing reads pixels back from it,
   * and that hint forces a software-backed surface which is slower to draw.
   */
  let scratchCanvas: HTMLCanvasElement | null = null;
  let scratchCtx: CanvasRenderingContext2D | null = null;

  function getScratch(): CanvasRenderingContext2D | null {
    if (!scratchCtx) {
      scratchCanvas = blankCanvas(CANVAS.width, CANVAS.height);
      scratchCtx = scratchCanvas.getContext("2d");
    }
    return scratchCtx;
  }

  const indexOf = (id: string) => layers.findIndex((l) => l.id === id);
  const find = (id: string) => layers.find((l) => l.id === id);

  /** `target` so merge-down can draw a placement into another layer's canvas
   *  through exactly the same clip-then-rotate path the composite uses. */
  function drawPlacement(target: CanvasRenderingContext2D, layer: RuntimeLayer) {
    const p = layer.placement;
    if (!p) return;
    const face = REGIONS[p.face];
    const r = resolvePlacement(p, layer.source.width / layer.source.height);

    const ctx = target;
    ctx.save();
    // Clip FIRST, rotate second. Reversing these two lines is the difference
    // between "a logo cannot leave its face" and "a logo usually stays put".
    ctx.beginPath();
    ctx.rect(face.x, face.y, face.w, face.h);
    ctx.clip();
    ctx.translate(r.cx, r.cy);
    ctx.rotate(r.rotation);
    ctx.drawImage(layer.source, -r.w / 2, -r.h / 2, r.w, r.h);
    ctx.restore();
  }

  /**
   * Where the below/above cache goes.
   *
   * **Not built.** `compose` below walks every layer on every recomposite,
   * which is fine at four layers and wrong at twenty: a brush stroke
   * recomposites its dirty rect through the entire stack, every frame.
   *
   * The fix, and what Krita's projection system amounts to, is to cache the
   * stack either side of the active layer:
   *
   *     belowCache  = layers[0 .. active-1]   composited once
   *     activeLayer = live
   *     aboveCache  = layers[active+1 .. n]   composited once
   *
   *     per frame:  below + active + overlay + above    ← 4 draws, never n
   *
   * Both caches invalidate only when the stack changes — reorder, visibility,
   * opacity, add, delete, or a change of active layer. **Painting must never
   * invalidate them**; that is the entire point, and it makes stroke cost
   * independent of layer count.
   *
   * It belongs here rather than in `compose` because the invalidation points
   * are the mutators below, and it must land before layer groups: a group turns
   * every naive composite into a tree walk.
   *
   * See docs/decisions/paint-features.md.
   */

  /**
   * Draw an artwork layer with its in-progress stroke folded in.
   *
   * The stroke used to go straight onto the composite, above the layer. That is
   * right for a brush and wrong for everything else: `destination-out` applied
   * to the composite cuts a hole through *every layer beneath*, when an eraser
   * should remove paint from its own layer and reveal what is under it. Same for
   * alpha lock — `source-atop` against the composite locks to whatever happens
   * to be underneath rather than to this layer's own pixels.
   *
   * So layer and stroke are combined in a scratch buffer first, and the result
   * is drawn as one image. The layer's own opacity and blend mode then apply to
   * that combined result, which is also the correct order: a stroke on a 50%
   * layer should end up 50%, not painted at full strength over a faded layer.
   */
  function drawArtworkWithStroke(
    layer: RuntimeLayer,
    rect: DirtyRect,
    target: CanvasRenderingContext2D = ctx,
  ) {
    const sctx = getScratch();
    if (!sctx || !scratchCanvas || !overlay) {
      // No scratch context available. Draw the layer without its preview rather
      // than throwing mid-stroke — a missing preview is recoverable, an
      // exception inside a pointermove handler is not.
      target.drawImage(layer.source, 0, 0);
      return;
    }

    sctx.save();
    sctx.beginPath();
    sctx.rect(rect.x, rect.y, rect.w, rect.h);
    sctx.clip();
    // Only the dirty rect is cleared, so this stays cheap on a long stroke.
    // Whatever is stale outside it never reaches the composite, which is
    // clipped to the same rect.
    sctx.clearRect(rect.x, rect.y, rect.w, rect.h);

    sctx.globalAlpha = 1;
    sctx.globalCompositeOperation = "source-over";
    sctx.drawImage(layer.source, 0, 0);

    // NaN or an out-of-range opacity from a caller would silently blank the
    // stroke; clamping is cheaper than debugging that.
    const alpha = overlay.opacity();
    sctx.globalAlpha = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
    sctx.globalCompositeOperation = overlay.mode();
    sctx.drawImage(overlay.source, 0, 0);
    sctx.restore();

    target.drawImage(scratchCanvas, 0, 0);
  }

  /** Recomposite `rect` (default: everything) and notify. */
  /**
   * Clip paths for face-restricted layers, built once per distinct restriction.
   *
   * `compose` runs on every frame of every stroke, and a Path2D of up to
   * eighteen rects per layer per frame is work with one answer. Keyed by the
   * face list itself, so changing a layer's faces produces a different key and
   * the stale path is simply never asked for again.
   */
  const clips = new Map<string, Path2D>();

  /** The clip for a layer, or null when it is unrestricted — the common case. */
  function faceClip(layer: RuntimeLayer): Path2D | null {
    // Absent OR empty means every face: the same convention `Selection` uses,
    // and the one that makes an old project with no field behave exactly as it
    // did before the field existed.
    if (!layer.faces || layer.faces.length === 0) return null;
    const key = layer.faces.join(",");
    const hit = clips.get(key);
    if (hit) return hit;
    const path = new Path2D();
    for (const r of paintRects(new Set(layer.faces))) path.rect(r.x, r.y, r.w, r.h);
    clips.set(key, path);
    return path;
  }

  /**
   * A second scratch, for clipping.
   *
   * Separate from the stroke scratch on purpose: a clipped layer that is being
   * painted needs both at once — one holding the layer plus its wet stroke, the
   * other holding that result masked by the base.
   */
  let clipCanvas: HTMLCanvasElement | null = null;
  let clipCtx: CanvasRenderingContext2D | null = null;
  function getClipScratch(): CanvasRenderingContext2D | null {
    if (clipCtx) return clipCtx;
    clipCanvas = document.createElement("canvas");
    clipCanvas.width = CANVAS.width;
    clipCanvas.height = CANVAS.height;
    clipCtx = clipCanvas.getContext("2d", { willReadFrequently: false });
    return clipCtx;
  }

  /**
   * The layer a clipped layer is masked by.
   *
   * The nearest one below that is not ITSELF clipped, so a run of clipped
   * layers all share one base — which is what every editor means by a clipping
   * group, and what stops a stack of three shading layers each masking the one
   * under it into nothing.
   *
   * Positional rather than stored by id, deliberately: drag a different layer
   * underneath and the clip follows, which is how Photoshop behaves and the only
   * rule that survives reordering without dangling.
   */
  function clipBaseFor(index: number): RuntimeLayer | null {
    for (let i = index - 1; i >= 0; i--) {
      const below = layers[i];
      if (below && !below.clipToBelow) return below;
    }
    return null;
  }

  /** One layer's pixels, into whichever context is given, honouring its faces. */
  function paintLayerInto(
    target: CanvasRenderingContext2D,
    layer: RuntimeLayer,
    rect: DirtyRect,
    wet: boolean,
  ) {
    const clip = faceClip(layer);
    if (clip) {
      target.save();
      target.clip(clip);
    }
    if (wet) drawArtworkWithStroke(layer, rect, target);
    else if (layer.kind === "artwork") target.drawImage(layer.source, 0, 0);
    else drawPlacement(target, layer);
    if (clip) target.restore();
  }

  function compose(rect: DirtyRect = FULL) {
    // A degenerate rect would clip everything away and clear nothing, leaving
    // the composite stale with no error. Callers derive rects from pointer
    // geometry, which routinely produces empty ones at the canvas edge.
    if (rect.w <= 0 || rect.h <= 0) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    ctx.clearRect(rect.x, rect.y, rect.w, rect.h);

    for (const [index, layer] of layers.entries()) {
      if (!layer.visible || layer.opacity <= 0) continue;

      /*
        A clipped layer hides with its base.

        Matching every editor: the base is what the clipped layer is drawn
        onto, so a hidden base leaves nothing to draw onto. A clipped layer
        with NO base — dragged to the bottom of the stack — draws normally
        rather than vanishing, because a layer that disappears when moved is
        harder to understand than one that stops being clipped.
      */
      const base = layer.clipToBelow ? clipBaseFor(index) : null;
      if (layer.clipToBelow && base && (!base.visible || base.opacity <= 0)) continue;

      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = blendComposite(layer.blendMode);

      // The scratch path costs an extra full-canvas draw, so it is taken only
      // while a stroke is actually in flight — not on every reorder and toggle.
      const wet =
        overlay !== null && layer.id === activeId && layer.kind === "artwork" && overlay.active();

      /*
        A layer restricted to some faces is clipped to them, here and nowhere
        else — so every path that draws it obeys the restriction, including
        export, which composites through this same function.

        Clipped rather than filtered per pixel: the restriction is rectangles,
        the canvas already does rectangles, and a layer whose pixels were
        physically erased outside its faces could not be un-restricted later.
        This keeps the artwork intact and changes only what is shown.
      */
      /*
        Clipped: build the layer on a scratch, punch it to the base's alpha,
        then composite that once with the layer's own blend and opacity.

        Not `source-atop` straight onto the canvas, which is the obvious version
        and is wrong: source-atop clips to everything accumulated so far, so the
        layer would also appear over any OTHER layer below that happens to have
        pixels there. The mask has to be the base alone.
      */
      const cs = base ? getClipScratch() : null;
      if (base && cs && clipCanvas) {
        cs.save();
        cs.beginPath();
        cs.rect(rect.x, rect.y, rect.w, rect.h);
        cs.clip();
        cs.clearRect(rect.x, rect.y, rect.w, rect.h);
        cs.globalAlpha = 1;
        cs.globalCompositeOperation = "source-over";
        paintLayerInto(cs, layer, rect, wet);
        // Keep only what the base covers. `drawPlacement` never touches the
        // composite operation, and save/restore preserves it, so this survives
        // whichever kind the base is.
        cs.globalCompositeOperation = "destination-in";
        paintLayerInto(cs, base, rect, false);
        cs.restore();
        ctx.drawImage(clipCanvas, 0, 0);
        continue;
      }

      paintLayerInto(ctx, layer, rect, wet);
    }

    /*
      The preview sits above everything and is recorded by nothing.

      It exists so a fabric can be seen on the body before it is chosen. Doing
      that by adding a real layer would put an entry in history for every hover,
      which is worse than no preview: undo would become a list of things the
      user never did.
    */
    if (preview) {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(preview, 0, 0);
    }

    ctx.restore();
    // restore() returns alpha and composite op to their saved values, but being
    // explicit costs nothing and this context is handed out via ctxFor().
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";

    opts.onChange?.(rect, layers);
  }

  function add(layer: RuntimeLayer, { activate = true } = {}): RuntimeLayer {
    layers.push(layer);
    if (activate) activeId = layer.id;
    compose();
    return layer;
  }

  return {
    /** The composite. Bind the viewer and export to this and nothing else. */
    canvas,

    layers: (): readonly RuntimeLayer[] => layers,

    /**
     * The layer list as it stands, safe to keep.
     *
     * Each entry is a **shallow copy**: the metadata is duplicated so a later
     * rename or hide cannot rewrite the past, while the canvas is shared,
     * because copying a 1.3 MB canvas per undo entry is exactly the cost the
     * rect-scoped design exists to avoid. A canvas is only genuinely retained
     * when its layer has been removed from the stack — which is the one case
     * where retaining it is the entire point.
     */
    snapshot(): RuntimeLayer[] {
      return layers.map((l) => ({ ...l }));
    },

    /**
     * Put the list back as it was.
     *
     * Copies again on the way in, so the entry stays a record of the past
     * rather than becoming an alias of the present.
     */
    restore(list: readonly RuntimeLayer[]) {
      layers.length = 0;
      for (const l of list) layers.push({ ...l });
      // Undoing an add leaves the active layer pointing at something that no
      // longer exists, and a null active layer silently swallows the next
      // stroke. Fall back to the top of the stack.
      if (!activeId || !find(activeId)) activeId = layers.at(-1)?.id ?? null;
      compose();
    },

    /**
     * Replace a rect of one layer's pixels.
     *
     * `putImageData` ignores the composite mode and replaces outright, which is
     * the correct and only useful semantic for undo: whatever was there is
     * gone, this is what was there before.
     */
    putPixels(id: string, rect: DirtyRect, data: ImageData) {
      const layer = find(id);
      if (!layer) return;
      layer.source.getContext("2d")?.putImageData(data, rect.x, rect.y);
      layer.rev += 1;
      /*
        A placement recomposites whole, not by rect.

        `rect` is in the LAYER's own pixels, and for an artwork layer those are
        the template's — so the rect means the same thing to `compose`. A
        placement's source is its image, a few dozen pixels that are then
        scaled and rotated onto a face: the same rect would recomposite a patch
        of the top-left corner and leave the placement itself stale.
      */
      if (layer.kind === "placement") compose();
      else compose(rect);
    },

    /**
     * Run a structural change and report what it did.
     *
     * History is recorded here rather than inside each operation so that
     * `layers.ts` never learns what undo is: it announces before-and-after, and
     * whoever cares subscribes. It also means one entry per user action, not
     * one per internal mutation — merge down is a delete and a pixel write, and
     * undoing half of it would be worse than not undoing it.
     *
     * Nested calls join the outer one for the same reason.
     */
    transaction<T>(label: string, run: () => T): T {
      if (inTransaction) return run();
      const before = layers.map((l) => ({ ...l }));
      inTransaction = true;
      try {
        return run();
      } finally {
        inTransaction = false;
        const after = layers.map((l) => ({ ...l }));
        if (structureDiffers(before, after)) opts.onStructure?.(label, before, after);
      }
    },

    /**
     * Show, or stop showing, a what-if above the stack.
     *
     * Recomposites immediately: a preview that waited for the next change would
     * appear a frame late and linger a frame long, which on a hover reads as
     * lag in both directions.
     */
    setPreview(source: HTMLCanvasElement | null) {
      if (preview === source) return;
      preview = source;
      compose();
    },

    /**
     * Note that a layer's pixels changed.
     *
     * The brush writes straight into the layer's canvas through `activeCtx()`,
     * so the stack never sees the write and cannot infer this. Called once per
     * dirty rect, which during a stroke is once a frame — the point is not to
     * count, only to differ from last time.
     */
    markPainted(id: string | null = activeId) {
      const layer = id ? find(id) : null;
      if (layer) layer.rev += 1;
    },

    get: (id: string) => find(id),
    active: () => (activeId ? (find(activeId) ?? null) : null),
    setActive(id: string | null) {
      activeId = id && find(id) ? id : null;
    },

    /**
     * The drawing target for the brush, or null when there is nothing paintable.
     *
     * Null for three reasons, and `paintBlockedReason()` says which. A brush
     * that quietly does nothing is indistinguishable from a broken brush — the
     * hidden-layer case especially, where the paint really is being written and
     * simply cannot be seen.
     */
    activeCtx(): CanvasRenderingContext2D | null {
      const layer = activeId ? find(activeId) : null;
      // biome-ignore lint/complexity/useOptionalChain: the null check also narrows `layer` for the three clauses after it.
      if (!layer || layer.kind !== "artwork" || !layer.visible || layer.opacity <= 0) return null;
      return layer.source.getContext("2d");
    },

    /**
     * Why painting is currently impossible, for the UI to say out loud.
     *
     * Photoshop refuses the stroke and explains; doing neither is the worst of
     * the three options. Nothing surfaces this yet — the tool options popover is
     * where it goes.
     */
    paintBlockedReason(): string | null {
      const layer = activeId ? find(activeId) : null;
      if (!layer) return "No layer selected.";
      if (layer.kind !== "artwork")
        return `${layer.name} is a placement — move or scale it instead.`;
      if (!layer.visible) return `${layer.name} is hidden.`;
      if (layer.opacity <= 0) return `${layer.name} is at zero opacity.`;
      return null;
    },

    /**
     * Bind the brush's wet canvas so an in-progress stroke previews correctly.
     *
     * Set once at startup. Everything is read through functions rather than
     * copied, because the brush owns these values and a stale copy shows the
     * wrong preview — which for `mode` would mean previewing a brush stroke and
     * committing an erase.
     */
    setStrokeOverlay(
      source: HTMLCanvasElement | null,
      handlers: {
        opacity?: () => number;
        /** Must be the same function the commit uses. See strokeCompositeMode. */
        mode?: () => StrokeMode;
        /** True only while a stroke is in flight. Gates the scratch path. */
        active?: () => boolean;
      } = {},
    ) {
      overlay = source
        ? {
            source,
            opacity: handlers.opacity ?? (() => 1),
            mode: handlers.mode ?? (() => "source-over"),
            active: handlers.active ?? (() => true),
          }
        : null;
    },

    /**
     * Pixels across the whole template.
     *
     * Import calls this rather than replacing anything: several artwork layers
     * are allowed precisely so nothing anyone made is silently destroyed.
     */
    addArtwork({
      name,
      image,
      link,
    }: {
      name: string;
      image?: CanvasImageSource;
      link?: { filename: string };
    }): RuntimeLayer {
      const source = image
        ? canvasFrom(image, CANVAS.width, CANVAS.height)
        : blankCanvas(CANVAS.width, CANVAS.height);
      return add({
        id: newId(),
        name,
        kind: "artwork",
        visible: true,
        opacity: 1,
        blendMode: "normal",
        rev: 0,
        source,
        ...(link ? { link } : {}),
      });
    },

    /** One image on one face. */
    addPlacement({
      name,
      image,
      width,
      height,
      placement,
    }: {
      name: string;
      image: CanvasImageSource;
      width: number;
      height: number;
      placement: FacePlacement;
    }): RuntimeLayer {
      return add({
        id: newId(),
        name,
        kind: "placement",
        visible: true,
        opacity: 1,
        blendMode: "normal",
        rev: 0,
        source: canvasFrom(image, width, height),
        placement: { ...placement },
      });
    },

    remove(id: string) {
      const i = indexOf(id);
      if (i === -1) return;
      layers.splice(i, 1);
      if (activeId === id) {
        // Select the layer that took its place, or the one below it. Leaving
        // nothing selected means the next brush stroke silently does nothing.
        activeId = layers[Math.min(i, layers.length - 1)]?.id ?? null;
      }
      compose();
    },

    /** Move a layer to an absolute index in paint order (0 = bottom). */
    move(id: string, to: number) {
      if (moveWithin(layers, indexOf(id), to)) compose();
    },

    /** Move a layer by a relative number of places. Used by the menu and keys. */
    moveBy(id: string, delta: number): boolean {
      const from = indexOf(id);
      if (from === -1) return false;
      if (!moveWithin(layers, from, from + delta)) return false;
      compose();
      return true;
    },

    /**
     * Copy a layer, directly above the original.
     *
     * The pixels are cloned rather than shared. A duplicate that shares a canvas
     * looks correct until the first stroke lands on both at once, which is the
     * kind of bug that gets reported as "my layers are haunted".
     */
    duplicate(id: string): RuntimeLayer | null {
      const i = indexOf(id);
      const source = layers[i];
      if (i === -1 || !source) return null;

      const copy: RuntimeLayer = {
        ...source,
        id: newId(),
        // "Logo copy", not "Logo (2)" — matches what the rest of the OS does.
        name: `${source.name} copy`,
        source: canvasFrom(source.source, source.source.width, source.source.height),
        ...(source.placement ? { placement: { ...source.placement } } : {}),
      };
      // A copy does not inherit the link: two layers following one file would
      // both update on save, and only one of them would be the one you meant.
      copy.link = undefined;

      layers.splice(i + 1, 0, copy);
      activeId = copy.id;
      compose();
      return copy;
    },

    /**
     * Flatten a layer into the one beneath it.
     *
     * Composited through the *upper* layer's opacity and blend, then landing as
     * a normal layer at full opacity — that is what merge means, and computing
     * it any other way changes the picture.
     *
     * Refused when the layer below is a placement: a placement's canvas is the
     * source image at its own size and angle, so there is no sensible way to
     * paint full-canvas pixels into it. The reason is returned rather than
     * swallowed so the menu can disable the item and say why.
     */
    mergeDown(id: string): { ok: true } | { ok: false; reason: string } {
      const i = indexOf(id);
      const upper = layers[i];
      const lower = layers[i - 1];
      if (i === -1 || !upper) return { ok: false, reason: "Layer not found." };
      if (!lower) return { ok: false, reason: "Nothing below to merge into." };
      if (lower.kind !== "artwork") {
        return { ok: false, reason: `${lower.name} is an image — flatten it first.` };
      }

      const target = lower.source.getContext("2d");
      if (!target) return { ok: false, reason: "2D context unavailable." };

      const plan = planMerge(upper, lower);
      if (!plan.ok) return plan;

      target.save();
      target.globalAlpha = upper.opacity;
      target.globalCompositeOperation = blendComposite(upper.blendMode);
      if (plan.maskToLower) {
        /*
          Build the clipped result on the scratch first, then composite that
          once. The mask has to be `lower` alone, which is why this cannot be
          `source-atop` straight onto the target — that would clip to
          everything already on `lower`'s canvas, which is the same thing here
          only by accident and stops being so the moment `lower` is not empty.
        */
        const cs = getClipScratch();
        if (!cs || !clipCanvas) {
          target.restore();
          return { ok: false, reason: "2D context unavailable." };
        }
        cs.save();
        cs.setTransform(1, 0, 0, 1, 0, 0);
        cs.globalAlpha = 1;
        cs.globalCompositeOperation = "source-over";
        cs.clearRect(0, 0, CANVAS.width, CANVAS.height);
        paintLayerInto(cs, upper, FULL, false);
        cs.globalCompositeOperation = "destination-in";
        paintLayerInto(cs, lower, FULL, false);
        cs.restore();
        target.drawImage(clipCanvas, 0, 0);
      } else {
        // Through `paintLayerInto`, so a face restriction is honoured here the
        // same way the renderer honours it. Drawing the source directly would
        // merge ink onto faces the layer was never allowed to touch.
        paintLayerInto(target, upper, FULL, false);
      }
      target.restore();
      lower.rev += 1;

      layers.splice(i, 1);
      if (activeId === upper.id) activeId = lower.id;
      compose();
      return { ok: true };
    },

    /**
     * Clip a layer to the one below, or stop.
     *
     * Recomposites everything rather than a rect: turning clipping on or off
     * changes where the layer appears across the whole canvas, and the union of
     * "everywhere it was" and "everywhere it now is" is the canvas.
     */
    setClipToBelow(id: string, clip: boolean) {
      const layer = find(id);
      if (!layer || Boolean(layer.clipToBelow) === clip) return;
      layer.clipToBelow = clip;
      compose();
    },

    /**
     * The layer a clipped layer is actually masked by, or null.
     *
     * Exposed so the panel can show the truth rather than the flag: a layer
     * carrying `clipToBelow` that has been dragged to the bottom has nothing to
     * clip to, and a row reading "Clipped" there would be describing something
     * that is not happening.
     */
    clipBaseOf(id: string): RuntimeLayer | null {
      const index = layers.findIndex((l) => l.id === id);
      if (index < 0 || !layers[index]?.clipToBelow) return null;
      return clipBaseFor(index);
    },

    setVisible(id: string, visible: boolean) {
      const layer = find(id);
      if (!layer || layer.visible === visible) return;
      layer.visible = visible;
      compose();
    },

    setOpacity(id: string, opacity: number) {
      const layer = find(id);
      if (!layer) return;
      layer.opacity = Math.max(0, Math.min(1, opacity));
      compose();
    },

    /**
     * Limit a layer to some faces, or lift the limit with an empty list.
     *
     * Assigns a NEW array rather than mutating the old one. `transaction`
     * snapshots layers with `{ ...l }`, so a shallow copy shares whatever array
     * the field points at — mutating in place would leave undo holding the
     * value it was supposed to restore.
     */
    setFaces(id: string, faces: readonly RegionId[]) {
      const layer = find(id);
      if (!layer) return;
      const next = [...faces].sort();
      if (facesKey(layer.faces) === facesKey(next)) return;
      // Normalised to `undefined` when empty, so "no restriction" has one
      // representation rather than two that behave the same but compare unequal.
      layer.faces = next.length > 0 ? next : undefined;
      compose();
    },

    /**
     * "Paint inside only" — keep strokes within what this layer already has.
     *
     * The composite mode both the preview and the commit resolve through
     * `strokeCompositeMode`, so turning it on here changes what the next stroke
     * does everywhere at once. Already compared by `structureDiffers`, so a
     * transaction around this is one undo step without further work.
     */
    setPaintInsideOnly(id: string, on: boolean) {
      const layer = find(id);
      if (!layer || Boolean(layer.paintInsideOnly) === on) return;
      layer.paintInsideOnly = on;
      // No recomposite: this changes what the NEXT stroke does, not any pixel
      // already on the canvas.
    },

    setBlendMode(id: string, blendMode: BlendMode) {
      const layer = find(id);
      if (!layer) return;
      layer.blendMode = blendMode;
      compose();
    },

    /**
     * Deliberately does not recomposite.
     *
     * A name is not a pixel. Routing this through `compose()` would repaint the
     * full 585×559 canvas and push a fresh texture to the 3D viewer because
     * someone typed a letter — so the panel redraws itself after a rename.
     */
    rename(id: string, name: string) {
      const layer = find(id);
      if (layer) layer.name = name;
    },

    /**
     * Nudge a placement. Recomposites the union of where it was and where it is
     * now — the old area has to be repainted or the layer leaves a smear.
     */
    setPlacement(id: string, patch: Partial<FacePlacement>) {
      const layer = find(id);
      if (!layer?.placement) return;
      const before = faceRectOf(layer.placement.face);
      layer.placement = { ...layer.placement, ...patch };
      const after = faceRectOf(layer.placement.face);
      compose(union(before, after));
    },

    /** Replace a layer's pixels in place — a live-linked file changing on disk. */
    setPixels(id: string, image: CanvasImageSource, width: number, height: number) {
      const layer = find(id);
      if (!layer) return;
      layer.source = canvasFrom(image, width, height);
      compose();
    },

    compose,
  };
}

/** A placement can only ever affect its own face, so that is the repaint area. */
function faceRectOf(face: RegionId): DirtyRect {
  const r = REGIONS[face];
  return { x: r.x, y: r.y, w: r.w, h: r.h };
}

function union(a: DirtyRect, b: DirtyRect): DirtyRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    w: Math.max(a.x + a.w, b.x + b.w) - x,
    h: Math.max(a.y + a.h, b.y + b.h) - y,
  };
}

export type LayerStack = ReturnType<typeof createLayerStack>;
